import React from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import moment from "moment";
import { shortLabel } from "../utils/policyParts";
import { STAGE_ORDER, insurerOf, nextStage, resetNote, setBackState, stageDetail, stageParts, stageStatusMeta, stageTitle } from "../utils/relianceStages";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";

// The History dialog's own palette (JobHistoryDialog TONE): done = green, failed = red, the next step = blue, later = grey
const LOOK = {
  completed: { color: "#067647", bg: "#ECFDF3", border: "#ABEFC6" },
  failed: { color: "#B42318", bg: "#FEF3F2", border: "#FECDCA" },
  next: { color: "#175CD3", bg: "#EFF8FF", border: "#B2DDFF" },
  pending: { color: "#667085", bg: "#F9FAFB", border: "#EAECF0" },
};


const smallBtn = {
  height: 28,
  minWidth: 0,
  whiteSpace: "nowrap",
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 600,
  fontFamily: FONT,
  px: "10px",
  borderRadius: "8px",
  boxShadow: "none",
};

/**
 * The confirm, in the card's own footer: "Only update" always; "Update & run" only when the policy was Completed,
 * Pending or Failed when it was checked just now (armed.canRun) — never while the automation runs it; ✕ cancels.
 */
const ArmedFooter = ({ row, armed, busy, onConfirm, onCancel }) => {
  if (armed.checking) {
    return (
      <Box data-testid={`stage-armed-${armed.partKey}-${row.stage}`} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={14} sx={{ color: NAVY }} />
        <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT }}>Checking the status…</Typography>
      </Box>
    );
  }
  return (
    <Box data-testid={`stage-armed-${armed.partKey}-${row.stage}`} sx={{ width: "100%" }}>
      <Typography sx={{ fontSize: "11px", color: "#475467", fontFamily: FONT, lineHeight: "15px", mb: "6px" }}>
        {resetNote(row.stage)}
        {!armed.canRun && armed.runWhy ? ` · ${armed.runWhy}` : ""}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Button
          variant="outlined"
          disabled={busy}
          onClick={() => onConfirm && onConfirm(false)}
          sx={{ ...smallBtn, color: NAVY, borderColor: "#C9D7E8", backgroundColor: "#fff" }}
        >
          Only update
        </Button>
        {armed.canRun ? (
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => onConfirm && onConfirm(true)}
            sx={{ ...smallBtn, backgroundColor: NAVY, "&:hover": { backgroundColor: "#16304F", boxShadow: "none" } }}
          >
            {busy ? "Saving…" : "Update & run"}
          </Button>
        ) : null}
        <Tooltip title="Cancel" arrow>
          <Box component="span" sx={{ ml: "auto" }}>
            <IconButton size="small" aria-label="Cancel" disabled={busy} onClick={() => onCancel && onCancel()} sx={{ width: 26, height: 26, color: "#667085" }}>
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

/** One step card. Every row starts on the same left edge; the footer is the same height in every card. */
const StageCard = ({ part, row, isNext, enabled, paymentMethod, armed, busy, onArm, onConfirm, onCancel, readOnly = false }) => {
  const meta = stageStatusMeta(row.status);
  const lookKey = meta.status === "pending" && isNext ? "next" : meta.status;
  const look = LOOK[lookKey];
  // read-only (View page): the status only — no pill click, no button, no footer
  const state = readOnly ? { ok: false, why: "" } : setBackState(row, { enabled, walletPaid: !!part.walletPaid, insurer: insurerOf(part) });
  const detail = stageDetail(row);
  const n = STAGE_ORDER.indexOf(row.stage) + 1;
  // the first click "arms" the card: the status is checked again and the footer turns into the confirm buttons
  const isArmed = !!armed && armed.partKey === part.key && armed.stage === row.stage;
  const pick = () => !isArmed && onArm && onArm(part, row);
  const pillLabel = lookKey === "next" ? "Up next" : meta.label;
  const filled = meta.status !== "pending";
  // set back with "Only update": the automation runs it when it starts or when its queue is empty
  const waitsForFree = !filled && !!part.runLater && part.runLater.stage === row.stage;

  return (
    <Box
      data-testid={`stage-card-${part.key}-${row.stage}`}
      sx={{
        minWidth: 0,
        p: readOnly ? "10px" : "14px",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: `1px solid ${look.border}`,
        borderTop: `3px solid ${filled || isNext ? look.color : look.border}`,
        backgroundColor: filled || isNext ? look.bg : "#FFFFFF",
      }}
    >
      {/* header: badge + title on the left, the status pill on the right — one line, centred */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minHeight: 28 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 700,
            fontFamily: FONT,
            boxSizing: "border-box",
            color: filled ? "#fff" : look.color,
            backgroundColor: filled ? look.color : "#fff",
            border: `2px solid ${look.color}`,
            "& svg": { fontSize: 17 },
          }}
        >
          {meta.status === "completed" ? <CheckRoundedIcon /> : meta.status === "failed" ? <CloseRoundedIcon /> : n}
        </Box>
        <Typography
          sx={{ flex: 1, minWidth: 0, fontSize: "14px", fontWeight: 700, color: NAVY, fontFamily: FONT, lineHeight: "20px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {stageTitle(row, { paymentMethod, company: part.company }).replace(/^\d+ /, "")}
        </Typography>
        <Tooltip title={state.why} arrow placement="top">
          <Box
            component={state.ok ? "button" : "span"}
            type={state.ok ? "button" : undefined}
            data-testid={`stage-${part.key}-${row.stage}`}
            aria-disabled={state.ok ? undefined : "true"}
            onClick={state.ok ? pick : undefined}
            sx={{
              flexShrink: 0,
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              height: 22,
              boxSizing: "border-box",
              border: `1px solid ${look.border}`,
              backgroundColor: "#fff",
              color: look.color,
              borderRadius: "999px",
              px: "8px",
              m: 0,
              font: "inherit",
              fontFamily: FONT,
              fontSize: "11.5px",
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: "nowrap",
              cursor: state.ok ? "pointer" : "default",
              "&:hover": state.ok ? { borderColor: look.color } : undefined,
            }}
          >
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: look.color }} />
            {pillLabel}
          </Box>
        </Tooltip>
      </Box>

      {/* what the step holds — the same left edge as the badge */}
      <Box sx={{ mt: readOnly ? "4px" : "10px", minHeight: readOnly ? 0 : 40 }}>
        {row.stage === "proposal" && row.proposalNumber ? (
          <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT, lineHeight: "18px" }}>
            Referral No{" "}
            <Box component="span" sx={{ fontWeight: 700, color: NAVY, letterSpacing: "0.02em" }}>
              {row.proposalNumber}
            </Box>
          </Typography>
        ) : null}
        {/* KSHEMA shows no number: its proposal is kept by its page, opened again on the next run */}
        {row.stage === "proposal" && !row.proposalNumber && row.link && meta.status === "completed" ? (
          <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT, lineHeight: "18px" }}>Proposal page saved</Typography>
        ) : null}
        {meta.status === "failed" && detail ? (
          <Tooltip title={detail} arrow placement="top">
            <Typography
              sx={{
                fontSize: "12px",
                lineHeight: "18px",
                color: LOOK.failed.color,
                fontFamily: FONT,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {detail}
            </Typography>
          </Tooltip>
        ) : null}
        {row.at && filled ? (
          <Typography sx={{ fontSize: "11.5px", color: "#667085", fontFamily: FONT, lineHeight: "18px" }}>
            {meta.status === "completed" ? "Done" : "Failed"} · {moment(row.at).format("DD MMM YYYY, hh:mm A")}
          </Typography>
        ) : null}
        {!filled ? (
          <Typography sx={{ fontSize: "12px", color: "#667085", fontFamily: FONT, lineHeight: "18px" }}>
            {waitsForFree
              ? "Set back with Only update - runs when the automation is free"
              : isNext
              ? "The next run starts here"
              : "Runs after the step before it"}
          </Typography>
        ) : null}
      </Box>

      {/* footer: always 32 px, so the cards end on one line; armed = the confirm buttons, in the same place */}
      {readOnly ? null : (
      <Box sx={{ mt: "auto", pt: "10px", borderTop: `1px solid ${filled || isNext ? look.border : "#F2F4F7"}`, minHeight: 32, display: "flex", alignItems: "center" }}>
        {isArmed ? (
          <ArmedFooter row={row} armed={armed} busy={busy} onConfirm={onConfirm} onCancel={onCancel} />
        ) : state.ok ? (
          <Button
            size="small"
            data-testid={`stage-${part.key}-${row.stage}-reset`}
            startIcon={<ReplayRoundedIcon sx={{ fontSize: "16px !important" }} />}
            onClick={pick}
            sx={{
              height: 30,
              whiteSpace: "nowrap",
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: FONT,
              color: NAVY,
              px: "10px",
              ml: "-2px",
              borderRadius: "8px",
              backgroundColor: "#fff",
              border: "1px solid #C9D7E8",
              "&:hover": { backgroundColor: "#EEF3F9" },
            }}
          >
            Set back to Pending
          </Button>
        ) : (
          <Typography sx={{ fontSize: "11.5px", color: "#98A2B3", fontFamily: FONT, lineHeight: "16px" }}>
            {meta.status === "pending"
              ? waitsForFree
                ? "Queued for when the automation starts or its queue is empty"
                : "Done by the automation"
              : state.why}
          </Typography>
        )}
      </Box>
      )}
    </Box>
  );
};

/**
 * Reliance in three stages (History dialog): per Reliance part, its Proposal / KYC / Payment as three equal step cards.
 * A Completed or Failed step can be set back to Pending (its status pill or its button arm it — onArm — and the
 * confirm buttons appear in the card's footer: onConfirm(run) / onCancel); a Pending
 * step has no action at all, so nothing can ever be set to Completed by hand.
 */
const RelianceStageList = ({ relianceStages, enabled = true, paymentMethod, armed = null, busy = false, onArm, onConfirm, onCancel, readOnly = false }) => {
  const parts = stageParts(relianceStages);
  if (!parts.length) return null;
  return (
    <Box data-testid="reliance-stages" sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #F2F4F7" }}>
      {parts.map((part) => {
        const done = part.stages.filter((s) => stageStatusMeta(s.status).status === "completed").length;
        const next = nextStage(part.stages);
        const all = done === part.stages.length;
        return (
          <Box key={part.key} sx={{ "& + &": { mt: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1.25 }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: NAVY, fontFamily: FONT, lineHeight: "20px" }}>
                {insurerOf(part)} {part.label || shortLabel(part.key)} — steps
              </Typography>
              <Box
                sx={{
                  fontSize: "11.5px",
                  fontWeight: 700,
                  fontFamily: FONT,
                  lineHeight: "20px",
                  color: all ? LOOK.completed.color : "#475467",
                  backgroundColor: all ? LOOK.completed.bg : "#F2F4F7",
                  borderRadius: "999px",
                  px: 1.25,
                  whiteSpace: "nowrap",
                }}
              >
                {done} of {part.stages.length} done
              </Box>
            </Box>
            {/* readOnly = the View page, where this sits in the narrower side: one under the other, each with the full width */}
            <Box sx={{ display: "grid", gridTemplateColumns: readOnly ? "minmax(0, 1fr)" : { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: readOnly ? "8px" : "12px", alignItems: "stretch" }}>
              {part.stages.map((row) => (
                <StageCard key={row.stage} part={part} row={row} isNext={row.stage === next} enabled={enabled} paymentMethod={paymentMethod} armed={armed} busy={busy} onArm={onArm} onConfirm={onConfirm} onCancel={onCancel} readOnly={readOnly} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default RelianceStageList;
